import { useCallback } from 'react';
import ReactGA from 'react-ga4';

interface AnalyticsEvent {
  category: string;
  action: string;
  label?: string;
  value?: number;
}


export const useAnalytics = () => {
  // Track custom events
  const trackEvent = useCallback((event: AnalyticsEvent) => {
    ReactGA.event({
      category: event.category,
      action: event.action,
      label: event.label,
      value: event.value,
    });
  }, []);

  // Track page views
  const trackPageView = useCallback((page: string, title?: string) => {
    ReactGA.send({ 
      hitType: 'pageview', 
      page,
      title: title || document.title
    });
  }, []);

  // Track section visibility
  const trackSectionView = useCallback((section: string) => {
    trackEvent({
      category: 'Engagement',
      action: 'Section View',
      label: section,
      value: 1
    });
  }, [trackEvent]);

  // Track feature interactions
  const trackFeatureInteraction = useCallback((feature: string, action: string = 'Click') => {
    trackEvent({
      category: 'Engagement',
      action: `Feature ${action}`,
      label: feature,
      value: 1
    });
  }, [trackEvent]);

  // Track FAQ interactions
  const trackFAQInteraction = useCallback((question: string, action: string = 'Expand') => {
    trackEvent({
      category: 'Engagement',
      action: `FAQ ${action}`,
      label: question,
      value: 1
    });
  }, [trackEvent]);

  // Track conversion events
  const trackConversion = useCallback((action: string, label: string, value?: number) => {
    trackEvent({
      category: 'Conversion',
      action,
      label,
      value: value || 1
    });
  }, [trackEvent]);

  // Track scroll depth
  const trackScrollDepth = useCallback((section: string, depth: number) => {
    trackEvent({
      category: 'Engagement',
      action: 'Scroll Depth',
      label: `${section} - ${depth}%`,
      value: depth
    });
  }, [trackEvent]);

  // Track performance metrics
  const trackPerformance = useCallback((metric: string, value: number) => {
    trackEvent({
      category: 'Performance',
      action: 'Web Vital',
      label: metric,
      value: Math.round(value)
    });
  }, [trackEvent]);

  // Track form interactions
  const trackFormInteraction = useCallback((formName: string, action: string) => {
    trackEvent({
      category: 'Conversion',
      action: `Form ${action}`,
      label: formName,
      value: 1
    });
  }, [trackEvent]);

  // Track CTA clicks
  const trackCTAClick = useCallback((ctaName: string, location: string) => {
    trackEvent({
      category: 'Conversion',
      action: 'CTA Click',
      label: `${ctaName} - ${location}`,
      value: 1
    });
  }, [trackEvent]);

  // Track language switching
  const trackLanguageSwitch = useCallback((fromLang: string, toLang: string) => {
    trackEvent({
      category: 'Engagement',
      action: 'Language Switch',
      label: `${fromLang} to ${toLang}`,
      value: 1
    });
  }, [trackEvent]);

  // Track time on page
  const trackTimeOnPage = useCallback((page: string, timeInSeconds: number) => {
    trackEvent({
      category: 'Engagement',
      action: 'Time on Page',
      label: page,
      value: timeInSeconds
    });
  }, [trackEvent]);

  return {
    trackEvent,
    trackPageView,
    trackSectionView,
    trackFeatureInteraction,
    trackFAQInteraction,
    trackConversion,
    trackScrollDepth,
    trackPerformance,
    trackFormInteraction,
    trackCTAClick,
    trackLanguageSwitch,
    trackTimeOnPage,
  };
};
